import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from './LanguageContext';
import { MapPinIcon, StarIcon } from './Icons';
const AVATAR_COLORS = ['bg-red-500', 'bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-orange-500', 'bg-teal-500'];
function GoogleIcon({
  className
}: {
  className?: string;
}) {
  return <svg className={className} viewBox="0 0 24 24" fill="none" data-id="element-876">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" data-id="element-877" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" data-id="element-878" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.96 10.96 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05" data-id="element-879" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" data-id="element-880" />
    </svg>;
}
function VerifiedBadge() {
  return <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" data-id="element-881">
      <circle cx="12" cy="12" r="10" fill="#4285F4" data-id="element-882" />
      <path d="M9 12l2 2 4-4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-883" />
    </svg>;
}
export function TestimonialsSection() {
  const {
    t
  } = useLanguage();
  return <section className="py-24 bg-brand-black relative overflow-hidden border-t border-white/5" data-id="element-884">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-885">
        {/* Header */}
        <div className="text-center mb-16" data-id="element-886">
          <h2 className="font-oswald text-4xl md:text-5xl font-bold text-white mb-4 uppercase" data-id="element-887">
            {t.testimonials.title}
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto" data-id="element-888">
            {t.testimonials.subtitle}
          </p>
          {/* Google aggregate rating */}
          <div className="flex items-center justify-center gap-3 mt-6" data-id="element-889">
            <GoogleIcon className="w-7 h-7" data-id="element-890" />
            <div className="flex gap-0.5" data-id="element-891">
              {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-5 h-5 text-brand-yellow fill-current" data-id="element-892" />)}
            </div>
            <span className="text-white font-bold text-lg" data-id="element-893">4.9</span>
            <span className="text-gray-400 text-sm" data-id="element-894">based on 200+ reviews</span>
          </div>
        </div>

        {/* Review Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-895">
          {t.testimonials.reviews.map((review: any, index: number) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.5,
          delay: index * 0.1
        }} className="bg-[#1a1a1a] border border-white/10 rounded-2xl p-6 hover:border-white/20 transition-all flex flex-col h-full group" data-id="element-896">
              {/* Top row: Avatar + Name + Google badge */}
              <div className="flex items-start gap-4 mb-4" data-id="element-897">
                {/* Avatar with initials */}
                <div className={`w-11 h-11 rounded-full ${AVATAR_COLORS[index % AVATAR_COLORS.length]} flex items-center justify-center flex-shrink-0 shadow-lg`} data-id="element-898">
                  <span className="text-white font-bold text-sm" data-id="element-899">
                    {review.name.split(' ').map((n: string) => n[0]).join('')}
                  </span>
                </div>

                <div className="flex-1 min-w-0" data-id="element-900">
                  <div className="flex items-center gap-2" data-id="element-901">
                    <p className="font-bold text-white text-sm truncate" data-id="element-902">
                      {review.name}
                    </p>
                    <VerifiedBadge data-id="element-903" />
                  </div>
                  <div className="flex items-center gap-1.5 mt-0.5" data-id="element-904">
                    <MapPinIcon className="w-3 h-3 text-gray-500 flex-shrink-0" data-id="element-905" />
                    <span className="text-gray-500 text-xs truncate" data-id="element-906">
                      {review.location}
                    </span>
                  </div>
                </div>

                {/* Google icon */}
                <GoogleIcon className="w-6 h-6 flex-shrink-0" data-id="element-907" />
              </div>

              {/* Stars + Date row */}
              <div className="flex items-center gap-3 mb-4" data-id="element-908">
                <div className="flex gap-0.5" data-id="element-909">
                  {[...Array(review.rating)].map((_: any, i: number) => <StarIcon key={i} className="w-4 h-4 text-brand-yellow fill-current" data-id="element-910" />)}
                </div>
                <span className="text-xs text-gray-500" data-id="element-911">{review.date}</span>
              </div>

              {/* Review text */}
              <blockquote className="flex-grow mb-4" data-id="element-912">
                <p className="text-gray-300 text-sm leading-relaxed" data-id="element-913">
                  {review.text}
                </p>
              </blockquote>

              {/* Footer: Posted on Google */}
              <div className="mt-auto pt-4 border-t border-white/5 flex items-center gap-2" data-id="element-914">
                <GoogleIcon className="w-4 h-4" data-id="element-915" />
                <span className="text-xs text-gray-500" data-id="element-916">Posted on Google</span>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}